import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Check, Coins, Calculator, Crown, Zap, MessageSquarePlus, Loader2, RefreshCw } from "lucide-react";
import { Header } from "@/components/Header";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PLANS = [
  { lookup_key: "plan_standard", name: "Monthly", price: "$149.99", cadence: "/mo", credits: 1000, note: "1,000 credits added every month" },
  { lookup_key: "plan_annual", name: "Annual — pay in full", price: "$1,649.89", cadence: "/yr", credits: 13200, note: "13,200 credits up front · best for busy teams", best: true, save: "Save $149.99/yr" },
];

export default function Store() {
  const { user, company, refresh } = useAuth();
  const isOwner = user.role === "owner";
  const [data, setData] = useState(null);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    api.get("/store/packs").then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    refresh?.();
  }, []); // eslint-disable-line

  const cpg = data?.credits_per_generation || 10;
  const balance = company?.credit_balance ?? data?.credit_balance ?? 0;

  const checkout = async (lookup_key) => {
    if (!isOwner) return toast.error("Only the account owner can purchase. Ask your owner to top up.");
    setBuying(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, quantity: 1, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBuying(null);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <div className="mx-auto max-w-6xl px-6 py-10 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="overline">Store</p>
            <h1 className="font-display text-3xl tracking-tight md:text-4xl">Fuel your team with credits</h1>
            <p className="mt-2 max-w-xl text-sm text-[var(--ink-soft)]">
              Every generation costs {cpg} credits. Credits <span className="font-semibold text-[var(--ink)]">roll over and never expire</span> — buy once, use whenever.
            </p>
          </div>
          <div data-testid="credit-balance" className="rounded-2xl border border-[var(--brand)]/25 bg-[var(--brand)]/[0.05] px-5 py-3">
            <p className="flex items-center gap-1.5 text-xs text-[var(--ink-soft)]"><Coins className="h-3.5 w-3.5 text-[var(--brand)]" /> Your balance</p>
            <p className="font-display text-2xl text-[var(--brand)]">{balance.toLocaleString()} <span className="text-sm font-normal text-[var(--ink-soft)]">credits</span></p>
            <p className="text-xs text-[var(--ink-soft)]">≈ {Math.floor(balance / cpg).toLocaleString()} generations</p>
          </div>
        </div>

        {/* Subscription plans */}
        {!company?.subscription_active && (
          <div className="mt-8">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--ink-soft)]">Subscribe</h2>
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {PLANS.map((p) => (
                <div key={p.lookup_key} data-testid={`plan-${p.lookup_key}`}
                  className={`relative rounded-2xl border-2 bg-white p-6 transition-all ${p.best ? "border-[var(--brand)] shadow-[0_10px_40px_rgba(192,57,43,0.12)]" : "border-[var(--line)]"}`}>
                  {p.best && <span className="absolute -top-3 left-6 rounded-full bg-[var(--brand)] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">{p.save}</span>}
                  <div className="flex items-center gap-2"><Crown className={`h-4 w-4 ${p.best ? "text-[var(--brand)]" : "text-[var(--ink-soft)]"}`} /><p className="font-semibold">{p.name}</p></div>
                  <p className="mt-2 font-display text-3xl">{p.price}<span className="text-base font-normal text-[var(--ink-soft)]">{p.cadence}</span></p>
                  <p className="mt-1 text-sm text-[var(--ink-soft)]">{p.note}</p>
                  {p.save && <p className="mt-1 text-sm font-semibold text-[var(--brand)]">{p.save} vs paying monthly</p>}
                  <button data-testid={`subscribe-${p.lookup_key}`} onClick={() => checkout(p.lookup_key)} disabled={buying === p.lookup_key}
                    className={`mt-4 w-full rounded-full py-3 text-sm font-semibold transition-colors disabled:opacity-60 ${p.best ? "bg-[var(--brand)] text-white hover:bg-[var(--brand-dark)]" : "border border-[var(--ink)] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-white"}`}>
                    {buying === p.lookup_key ? "Redirecting…" : "Choose plan"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Estimator — only for the first two top-ups, then hide for good */}
        {(data?.pack_purchase_count || 0) < 2 && (
          <Estimator cpg={cpg} packs={data?.packs || []} firstPack={data?.first_pack_credits} />
        )}

        {/* Upsell banner */}
        {data?.should_upsell && (() => {
          const up = (data.packs || []).find((p) => p.lookup_key === data.upsell_lookup_key);
          if (!up) return null;
          return (
            <div data-testid="upsell-banner" className="mt-8 flex flex-col items-start justify-between gap-3 rounded-2xl border-2 border-[var(--brand)] bg-[var(--brand)]/[0.06] p-5 sm:flex-row sm:items-center">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--brand)]/15 text-[var(--brand)]"><Crown className="h-5 w-5" /></span>
                <div>
                  <p className="text-sm font-semibold">You're flying through your credits 🚀</p>
                  <p className="text-xs text-[var(--ink-soft)]">Ready to scale up? The <span className="font-semibold text-[var(--ink)]">{up.tier}</span> pack gives you {up.credits.toLocaleString()} credits.</p>
                </div>
              </div>
              <button data-testid="upsell-buy-btn" onClick={() => checkout(up.lookup_key)} disabled={buying === up.lookup_key}
                className="shrink-0 rounded-full bg-[var(--brand)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-60">
                Get {up.tier} · ${up.price.toFixed(2)}
              </button>
            </div>
          );
        })()}

        {/* Credit packs */}
        <h2 className="mt-10 text-sm font-semibold uppercase tracking-wide text-[var(--ink-soft)]">Top up credits</h2>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(data?.packs || []).map((p) => {
            const highlight = p.badge === "Recommended";
            const best = p.badge === "Best Value";
            return (
              <div key={p.lookup_key} data-testid={`pack-${p.lookup_key}`}
                className={`group relative flex flex-col rounded-2xl border-2 bg-white p-6 transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_16px_50px_rgba(0,0,0,0.10)] ${
                  best ? "border-[var(--brand)] shadow-[0_10px_40px_rgba(192,57,43,0.10)]" : highlight ? "border-[var(--ink)]" : "border-[var(--line)]"}`}>
                {p.badge && (
                  <span className={`absolute -top-3 left-6 flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white ${best ? "bg-[var(--brand)]" : "bg-[var(--ink)]"}`}>
                    {best ? <Crown className="h-3 w-3" /> : <Zap className="h-3 w-3" />} {p.badge}
                  </span>
                )}
                <p className="text-base font-semibold text-[var(--ink-soft)]">{p.tier}</p>
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[var(--brand)]/10 px-4 py-2 text-[var(--brand)]">
                  <Coins className="h-6 w-6" />
                  <span className="font-display text-3xl leading-none">{p.credits.toLocaleString()}</span>
                  <span className="text-base font-semibold">credits</span>
                </div>
                <p className="mt-3 font-display text-2xl text-[var(--ink)]">= {p.generations.toLocaleString()} <span className="text-lg font-normal text-[var(--ink-soft)]">generations</span></p>
                <div className="my-4 h-px bg-[var(--line)]" />
                <p className="font-display text-2xl">${p.price.toFixed(2)}</p>
                <button data-testid={`buy-${p.lookup_key}`} onClick={() => checkout(p.lookup_key)} disabled={buying === p.lookup_key}
                  className={`mt-5 w-full rounded-full py-3 text-sm font-semibold transition-colors disabled:opacity-60 ${
                    best ? "bg-[var(--brand)] text-white hover:bg-[var(--brand-dark)]" : "border border-[var(--ink)] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-white"}`}>
                  {buying === p.lookup_key ? "Redirecting…" : "Buy credits"}
                </button>
              </div>
            );
          })}
        </div>

        {/* Auto top-up */}
        <AutoTopup packs={data?.packs || []} company={company} refresh={refresh} isOwner={isOwner} />

        {/* Help CTA */}
        <HelpCard />

        <p className="mt-6 text-center text-xs text-[var(--ink-soft)]">Payments are processed securely by Stripe. Credits are added to your account the moment your payment completes.</p>
      </div>
    </div>
  );
}

function Estimator({ cpg, packs, firstPack }) {
  const [reps, setReps] = useState(3);
  const est = useMemo(() => {
    const n = Math.max(1, Math.min(Number(reps) || 1, 500));
    // 2-3 leads/day × 6-10 images = 12-30 gens/rep/day × 5-6 days/wk × 4.33 wks
    const lowGens = n * 12 * 5 * 4.33;
    const highGens = n * 30 * 6 * 4.33;
    // take the midpoint and round DOWN to the nearest 100
    const midGens = Math.floor(((lowGens + highGens) / 2) / 100) * 100;
    const midCr = midGens * cpg;
    // smallest pack that covers the monthly need
    let needIdx = packs.findIndex((p) => p.credits >= midCr);
    if (needIdx === -1) needIdx = packs.length - 1;
    // first-time buyers get nudged one tier smaller (less scary, top up anytime)
    const firstTime = !firstPack;
    const recIdx = firstTime ? Math.max(0, needIdx - 1) : needIdx;
    return { n, midGens, midCr, rec: packs[recIdx], firstTime };
  }, [reps, cpg, packs, firstPack]);

  return (
    <div data-testid="estimator" className="mt-8 rounded-2xl border border-[var(--line)] bg-[var(--bone)] p-6">
      <h2 className="flex items-center gap-2 text-sm font-semibold"><Calculator className="h-4 w-4 text-[var(--brand)]" /> Not sure how many credits you need?</h2>
      <p className="mt-1 text-sm text-[var(--ink-soft)]">We assume each rep runs 2–3 leads a day and creates 6–10 images per appointment, 5–6 days a week.</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <label className="text-sm font-medium">Team size</label>
        <input data-testid="estimator-reps" type="number" min={1} max={500} value={reps} onChange={(e) => setReps(e.target.value)}
          className="w-24 rounded-lg border border-[var(--line)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--brand)]" />
        <span className="text-sm text-[var(--ink-soft)]">sales reps</span>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-xl border-2 border-[var(--brand)]/30 bg-white p-4">
          <p className="text-xs text-[var(--ink-soft)]">Credits you'll use / month</p>
          <p className="font-display text-3xl text-[var(--brand)]" data-testid="estimator-credits">{est.midCr.toLocaleString()}</p>
          <p className="text-xs text-[var(--ink-soft)]">that's about {est.midGens.toLocaleString()} generations</p>
        </div>
        <div className="rounded-xl border border-[var(--line)] bg-white p-4">
          <p className="text-xs text-[var(--ink-soft)]">Cost per generation</p>
          <p className="font-display text-3xl" data-testid="estimator-gens">{cpg}<span className="ml-1 text-base font-normal text-[var(--ink-soft)]">credits</span></p>
        </div>
        <div className="rounded-xl border border-[var(--brand)]/30 bg-[var(--brand)]/[0.05] p-4">
          <p className="text-xs text-[var(--ink-soft)]">{est.firstTime ? "Start here" : "Our pick for you"}</p>
          <p className="font-display text-2xl text-[var(--brand)]" data-testid="estimator-rec">{est.rec ? est.rec.tier : "—"}</p>
          {est.rec && <p className="text-xs text-[var(--ink-soft)]">{est.rec.credits.toLocaleString()} credits · ${est.rec.price.toFixed(2)}{est.firstTime ? " · top up anytime" : ""}</p>}
        </div>
      </div>
    </div>
  );
}

function AutoTopup({ packs, company, refresh, isOwner }) {
  const at = company?.auto_topup || { enabled: false };
  const [enabled, setEnabled] = useState(!!at.enabled);
  const [lookup, setLookup] = useState(at.lookup_key || packs[0]?.lookup_key || "");
  const [threshold, setThreshold] = useState(at.threshold || 100);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setEnabled(!!at.enabled);
    setLookup(at.lookup_key || packs[0]?.lookup_key || "");
    setThreshold(at.threshold || 100);
  }, [company?.auto_topup, packs.length]); // eslint-disable-line

  if (!isOwner) return null;
  const save = async (nextEnabled) => {
    setSaving(true);
    try {
      await api.patch("/settings/auto-topup", { enabled: nextEnabled, lookup_key: lookup, threshold: Number(threshold) });
      await refresh?.();
      toast.success(nextEnabled ? "Auto top-up is on — we'll refill before you run out." : "Auto top-up turned off.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="auto-topup-card" className="mt-8 rounded-2xl border border-[var(--line)] bg-white p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold"><RefreshCw className="h-4 w-4 text-[var(--brand)]" /> Auto top-up</h2>
          <p className="mt-1 text-sm text-[var(--ink-soft)]">Never let your team get blocked. Pick a pack and a trigger — when your balance drops to that level, we'll buy it automatically on your saved card.</p>
        </div>
        <button data-testid="auto-topup-toggle" onClick={() => save(!enabled)} disabled={saving}
          className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${enabled ? "bg-[var(--brand)]" : "bg-[var(--line)]"}`}>
          <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${enabled ? "left-[22px]" : "left-0.5"}`} />
        </button>
      </div>
      <div className={`mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 ${enabled ? "" : "pointer-events-none opacity-50"}`}>
        <div>
          <label className="text-xs font-medium text-[var(--ink-soft)]">Pack to buy</label>
          <select data-testid="auto-topup-pack" value={lookup} onChange={(e) => setLookup(e.target.value)}
            className="mt-1 w-full rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2 text-sm outline-none focus:border-[var(--brand)]">
            {packs.map((p) => <option key={p.lookup_key} value={p.lookup_key}>{`${p.tier} — ${p.credits.toLocaleString()} credits ($${p.price.toFixed(2)})`}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-[var(--ink-soft)]">When balance drops to</label>
          <div className="mt-1 flex items-center gap-2">
            <input data-testid="auto-topup-threshold" type="number" min={10} value={threshold} onChange={(e) => setThreshold(e.target.value)}
              className="w-28 rounded-lg border border-[var(--line)] bg-[var(--bone)] px-3 py-2 text-sm outline-none focus:border-[var(--brand)]" />
            <span className="text-sm text-[var(--ink-soft)]">credits</span>
          </div>
        </div>
      </div>
      {enabled && (
        <button data-testid="auto-topup-save" onClick={() => save(true)} disabled={saving}
          className="mt-4 rounded-full bg-[var(--ink)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:opacity-60">
          {saving ? "Saving…" : "Save auto top-up"}
        </button>
      )}
    </div>
  );
}

function HelpCard() {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!text.trim()) return toast.error("Add the product details you'd like help with.");
    setSending(true);
    try {
      await api.post("/messages", { text: `🛟 Product help / quote request:\n${text.trim()}` });
      toast.success("Sent! We'll reply in your inbox shortly.");
      setText("");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };
  return (
    <div data-testid="help-card" className="mt-8 rounded-2xl border border-[var(--line)] bg-white p-6">
      <h2 className="flex items-center gap-2 text-sm font-semibold"><MessageSquarePlus className="h-4 w-4 text-[var(--brand)]" /> Need help adding your products?</h2>
      <p className="mt-1 text-sm text-[var(--ink-soft)]">Send us your product info (or a link) and let us know you'd like a quote — it goes straight to our inbox and we'll take care of it.</p>
      <textarea data-testid="help-text" value={text} onChange={(e) => setText(e.target.value)} rows={3}
        placeholder="e.g. Here's our cabinet line + finishes (link or list). Please add them and send me a quote."
        className="mt-3 w-full resize-none rounded-xl border border-[var(--line)] bg-[var(--bone)] p-3 text-sm outline-none focus:border-[var(--brand)]" />
      <button data-testid="help-send-btn" onClick={send} disabled={sending}
        className="mt-3 flex items-center gap-2 rounded-full bg-[var(--brand)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-dark)] disabled:opacity-60">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Send to inbox
      </button>
    </div>
  );
}
